import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import {
  AdminTable, AdminSectionHeader, inputCls, labelCls, tdCls, InlineError,
} from '@/components/admin/AdminShared';

/**
 * Majors (categories) — Level-1 admin CRUD. Courses are grouped by these on
 * the homepage and courses page.
 */
export default function AdminCategories() {
  const utils = trpc.useUtils();
  const { data: categories = [] } = trpc.categories.list.useQuery();
  const refresh = () => utils.categories.list.invalidate();

  const create = trpc.categories.create.useMutation({
    onSuccess: () => { refresh(); setName(''); },
  });
  const update = trpc.categories.update.useMutation({
    onSuccess: () => { refresh(); setEditId(null); },
  });
  const del = trpc.categories.delete.useMutation({ onSuccess: () => refresh() });

  const [name,     setName]     = useState('');
  const [editId,   setEditId]   = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const submitCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    create.mutate({ name: name.trim(), order: categories.length });
  };

  // Reorder by swapping `order` with the neighbour, then re-normalising all
  // rows to their list index so ties never accumulate.
  const move = async (id: number, dir: -1 | 1) => {
    const ids = categories.map(c => c.id);
    const idx = ids.indexOf(id);
    const swap = idx + dir;
    if (idx < 0 || swap < 0 || swap >= ids.length) return;
    [ids[idx], ids[swap]] = [ids[swap], ids[idx]];
    await Promise.all(ids.map((cid, i) => update.mutateAsync({ id: cid, order: i })));
    refresh();
  };

  return (
    <div>
      <AdminSectionHeader title="Majors" count={categories.length} />

      {/* Add */}
      <form onSubmit={submitCreate}
            className="bg-[#09090B] border border-yellow-400/20 border-dashed rounded-3xl p-5 mb-8
                       flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1">
          <label className={labelCls}>New major</label>
          <input value={name} onChange={e => setName(e.target.value)}
                 placeholder="e.g. Chemical Engineering" className={inputCls} />
        </div>
        <button type="submit" disabled={create.isPending}
                className="bg-yellow-400 text-black font-black px-6 py-2.5 rounded-xl
                           text-sm transition-colors disabled:opacity-50">
          {create.isPending ? 'Adding…' : 'Add Major'}
        </button>
      </form>
      {create.error && <InlineError message={create.error.message} />}

      {/* List */}
      <AdminTable headers={['Order', 'Major', 'Courses', 'Actions']} empty={categories.length === 0}>
        {categories.map((cat, idx) => (
          <tr key={cat.id} className="border-t border-white/[0.04]">
            <td className={tdCls}>
              <div className="flex flex-col gap-0.5">
                <button onClick={() => move(cat.id, -1)} disabled={idx === 0 || update.isPending}
                        title="Move up"
                        className="w-6 h-5 flex items-center justify-center rounded text-[#A1A1AA]
                                   hover:text-white hover:bg-white/10 disabled:opacity-25 transition-colors">▲</button>
                <button onClick={() => move(cat.id, 1)} disabled={idx === categories.length - 1 || update.isPending}
                        title="Move down"
                        className="w-6 h-5 flex items-center justify-center rounded text-[#A1A1AA]
                                   hover:text-white hover:bg-white/10 disabled:opacity-25 transition-colors">▼</button>
              </div>
            </td>
            <td className={tdCls}>
              {editId === cat.id ? (
                <input value={editName} onChange={e => setEditName(e.target.value)}
                       className={inputCls} autoFocus />
              ) : (
                <span className="text-white font-semibold text-sm">{cat.name}</span>
              )}
            </td>
            <td className={`${tdCls} text-[#A1A1AA]`}>{(cat as any).courses?.length ?? '—'}</td>
            <td className={tdCls}>
              <div className="flex gap-2 items-center flex-wrap">
                {editId === cat.id ? (
                  <>
                    <button
                      onClick={() => update.mutate({ id: cat.id, name: editName.trim() })}
                      disabled={update.isPending}
                      className="text-xs text-yellow-400 border border-yellow-400/30 px-3 py-1.5 rounded-lg
                                 hover:bg-yellow-400/10 transition-colors disabled:opacity-50">
                      Save
                    </button>
                    <button onClick={() => setEditId(null)}
                            className="text-xs text-[#71717A] border border-white/10 px-3 py-1.5 rounded-lg
                                       hover:text-white transition-colors">
                      Cancel
                    </button>
                  </>
                ) : (
                  <button onClick={() => { setEditId(cat.id); setEditName(cat.name); }}
                          className="text-xs text-[#A1A1AA] hover:text-white border border-white/10
                                     hover:border-white/20 px-3 py-1.5 rounded-lg transition-colors">
                    Edit
                  </button>
                )}

                {deleteId === cat.id ? (
                  <div className="flex gap-1.5 items-center">
                    <span className="text-[#71717A] text-xs">Sure?</span>
                    <button onClick={() => { del.mutate({ id: cat.id }); setDeleteId(null); }}
                            className="text-xs text-red-400 border border-red-400/30 px-2.5 py-1 rounded-lg
                                       hover:bg-red-400/10 transition-colors">Yes</button>
                    <button onClick={() => setDeleteId(null)}
                            className="text-xs text-[#71717A] border border-white/10 px-2.5 py-1 rounded-lg
                                       hover:text-white transition-colors">No</button>
                  </div>
                ) : (
                  <button onClick={() => setDeleteId(cat.id)}
                          className="text-xs text-red-400 border border-red-400/20 hover:border-red-400/40
                                     px-3 py-1.5 rounded-lg transition-colors">
                    Delete
                  </button>
                )}
              </div>
            </td>
          </tr>
        ))}
      </AdminTable>
      <p className="text-[#52525B] text-xs mt-3">
        Deleting a major doesn't delete its courses — they just become uncategorised.
      </p>
    </div>
  );
}
